import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from '../base';
import { CreateObject } from '../item';
import { Place } from './place';

import { AuditView, AuditViewProxy } from '../views/audit';

/****************************/
/* TYPES                    */
/****************************/

export const AUDIT_INFO_SCHEMA_VERSION = 1;

/** Client-only sentinel for {@link AuditView} live report; never persisted in {@link Audit.report}. */
export const AUDIT_INFO_PRELIMINARY_SCHEMA_VERSION = 0;

export type AuditInfoPayments = {
    cash: number;
    card: number;
    line: number;
};

export type AuditInfoCountIncome = {
    amount: number;
    income: number;
};

export type AuditInfoFiscalCounters = {
    ok: number;
    ko: number;
};

/** Per-rate VAT row stored in {@link Audit.report} JSON (`taxBreakdown`). */
export type AuditInfoTaxBreakdown = {
    rate: number;
    base: number;
    taxes: number;
    total: number;
};

export type AuditInfoLineEntry = {
    amount: number;
    income: number;
};

export type AuditInfoLineActivity = {
    addlines: AuditInfoLineEntry;
    dellines: AuditInfoLineEntry;
};

/** Till-close report payload ({@link AuditInfoReport}). */
export type AuditInfoReport = {
    schemaVersion: number;
    payments: AuditInfoPayments;
    tickets: {
        completed: number;
        cancelled: number;
    };
    items: {
        completed: AuditInfoCountIncome;
        cancelled: AuditInfoCountIncome;
        onthehouse: AuditInfoCountIncome;
    };
    activity: AuditInfoLineActivity;
    fiscal: {
        ticketbai: AuditInfoFiscalCounters;
        verifactu: AuditInfoFiscalCounters;
        ticketsii: AuditInfoFiscalCounters;
    };
    taxBreakdown?: AuditInfoTaxBreakdown[];
};

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PlaceType } from '../types';
import { SessionType } from '../types';
import { TillType } from '../types';
import { TillChangeType } from '../types';

const _schema = {
    name: 'AUDIT',
    fields: [
        { name: 'created', type: 'date', volatile: true },
        { name: 'updated', type: 'date', volatile: true },
        { name: 'status', type: 'string', default: 'AC' },
        { name: 'place', type: 'objid' },
        { name: 'session', type: 'objid' },
        { name: 'cash', type: 'number', default: '0' },
        { name: 'card', type: 'number', default: '0' },
        { name: 'till', type: 'objid' },
        { name: 'ini', type: 'date' },
        { name: 'end', type: 'date' },
        { name: 'sequence', type: 'number', volatile: true },
        { name: 'report', type: 'string', volatile: true },
    ],
    relate: [
        { direction: '>', target: 'PLACE', by: 'place', name: 'place', reason: 'place', child: false, class: PlaceType },
        { direction: '>', target: 'SESSION', by: 'session', name: 'session', reason: 'session', child: false, class: SessionType },
        { direction: '>', target: 'TILL', by: 'till', name: 'till', reason: 'till', child: false, class: TillType },
        { direction: '<', target: 'TILLCHANGE', by: 'audit', name: 'tillchange', reason: 'tillchanges', child: true, class: TillChangeType },
    ],
} as const;

const _AuditClass = CreateObject(_schema);
if (!_AuditClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class Audit extends _AuditClass {
    private _report: AuditInfoReport | null | undefined = null;

    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): Audit {
        return this._Copy(store) as Audit;
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf){
            return;
        }

        if (relation === 'place' && related instanceof Place){
            if (related === this.place){
                related.AddAudit(this);
            }
            else {
                related.DelAudit(this);
            }
        }
    }

    protected override OnUpdate(info: any): boolean {
        this._report = null;
        return super.OnUpdate(info);
    }

    override get viewProxy(): AuditView {
        return AuditViewProxy(this, this.data);
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/

    /** Open-period draft (`PR`); till close sets `AC` before commit. */
    get IsPending(): boolean {
        return this.status === 'PR';
    }

    /** Parsed {@link AuditInfoReport} from synced {@link Audit.report} JSON. */
    get Report(): AuditInfoReport | null {
        if (this._report !== null){
            return this._report ?? null;
        }

        if (this.report){
            try {
                const _parsed = JSON.parse(this.report);
                if (_parsed.schemaVersion !== AUDIT_INFO_SCHEMA_VERSION){
                    console.error(`[AUDIT] Invalid report schemaVersion for audit: expected ${AUDIT_INFO_SCHEMA_VERSION}, got ${_parsed.schemaVersion}`,);
                }                
                else {
                    this._report = _parsed  as AuditInfoReport;
                }
            }
            catch {
                this._report = undefined;
                console.error(`[AUDIT] Invalid report JSON for audit: ${this.report}`);
            }
        }

        return this._report ?? null;
    }
}
